import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { withdrawalsApi, type WithdrawalRequest } from '../../api/withdrawals.api.js';
import { CounterpartyHistoryPanel } from '../../components/CounterpartyHistoryPanel.js';
import { RaiseSuspicionPanel } from '../../components/RaiseSuspicionPanel.js';
import { ReviewDocumentsPanel } from '../../components/ReviewDocumentsPanel.js';
import { PaymentConfirmationPanel } from '../../components/PaymentConfirmationPanel.js';
import { CommentsPanel } from '../../components/CommentsPanel.js';
import { OutgoingCommissionPanel } from '../../components/OutgoingCommissionPanel.js';
import { PaymentScreeningPanel } from '../../components/PaymentScreeningPanel.js';
import { useAuth } from '../../auth/useAuth.js';

/**
 * The queue staff work when money has to leave a bank we operate by hand.
 *
 * The screen exists because nothing else witnesses this transfer. No partner
 * confirms it, no webhook arrives; the only record that a customer's money was
 * sent is what a member of staff types here after doing it at the bank.
 *
 * So the one thing the screen must never leave ambiguous is whether the money
 * has moved. Three separate facts, in order:
 *
 *   Requested  — nothing has moved. The customer's balance is untouched.
 *   Approved   — the customer has been debited. The bank has not been told yet.
 *   Sent       — staff sent it at the bank, and gave us the bank's reference.
 *
 * The gap between Approved and Sent is the dangerous one: our books say the
 * money is gone and the bank has never heard of it. That gap is why an approved
 * row shouts for its bank reference until it gets one, and why "Sent" cannot be
 * recorded without it — a transfer nobody can trace is a transfer nobody can
 * prove happened.
 */

const FILTERS: readonly { value: string; label: string }[] = [
  { value: 'REQUESTED', label: 'Waiting on a decision' },
  { value: 'APPROVED', label: 'Debited — waiting to be sent' },
  { value: 'SUBMITTED', label: 'Processing at bank' },
  { value: 'EXECUTION_UNKNOWN', label: 'Bank reconciliation required' },
  { value: 'APPROVAL_UNKNOWN', label: 'Ledger reconciliation required' },
  { value: 'BANK_REJECTED', label: 'Bank rejected — refund required' },
  { value: 'SENT', label: 'Sent at the bank' },
  { value: 'REJECTED', label: 'Refused' },
  { value: 'FAILED', label: 'Failed at the bank' },
  { value: 'ALL', label: 'Everything' },
];

type ActionKind = 'approve' | 'sent' | 'reject' | 'failed';

import { formatTransactionAmount as money } from '../../utils/transaction-money.js';
import { APP_LOCALE } from '../../utils/locale.js';

function statusColor(status: string): 'default' | 'info' | 'warning' | 'success' | 'error' {
  switch (status) {
    case 'REQUESTED':
      return 'warning';
    case 'APPROVED':
      return 'info';
    case 'SENT':
      return 'success';
    case 'FAILED':
      return 'error';
    default:
      return 'default';
  }
}

/** What has actually happened to the money, in a sentence an operator can act on. */
function whereTheMoneyIs(status: string): string {
  switch (status) {
    case 'REQUESTED':
      return 'Nothing has moved. The customer still has the funds.';
    case 'APPROVED':
      return 'The customer has been debited. Bank execution is awaiting confirmation.';
    case 'SUBMITTING': return 'Submission is in progress. Do not send another transfer.';
    case 'SUBMITTED': return 'The bank has accepted the instruction; final confirmation is pending.';
    case 'EXECUTION_UNKNOWN': return 'The bank outcome is unknown. Reconcile before any retry or refund; do not send this transfer manually.';
    case 'APPROVAL_UNKNOWN': return 'The ledger debit could not be confirmed. Reconcile the recorded request before sending or refunding it.';
    case 'REFUNDING': return 'The refund is in progress or needs ledger reconciliation. Do not issue a second refund.';
    case 'BANK_REJECTED': return 'The bank confirmed rejection. Review the refund to the customer.';
    case 'SENT':
      return 'Sent at the bank.';
    case 'REJECTED':
      return 'Refused. Nothing was debited.';
    case 'FAILED':
      return 'The bank refused it. The money was returned to the customer.';
    default:
      return '';
  }
}

const TITLES: Record<ActionKind, string> = {
  approve: 'Approve this withdrawal',
  sent: 'Record that it was sent',
  reject: 'Refuse this withdrawal',
  failed: 'Record that the bank refused it',
};

export function WithdrawalsPage({
  reviewId,
  onReviewClose,
}: { reviewId?: string; onReviewClose?: () => void } = {}): JSX.Element {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  // DEV-67: the MLRO reviews (approve, refuse, ask); the client manager sends a
  // reviewed transfer and records the bank's answer. Everyone else reads.
  const canReview = can('payments.review');
  const canSend = can('payments.send');
  const canWork = canReview || canSend;

  const [filter, setFilter] = useState('REQUESTED');
  const [selected, setSelected] = useState<WithdrawalRequest | null>(null);
  const [action, setAction] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');
  const [screeningReady, setScreeningReady] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [question, setQuestion] = useState('');

  const queue = useQuery({
    queryKey: ['withdrawals', filter],
    queryFn: () => withdrawalsApi.list(filter),
    enabled: !reviewId,
    // Requests arrive while the screen is open, and a customer waiting on money
    // is not helped by a stale list.
    refetchInterval: 30_000,
  });

  const detail = useQuery({
    queryKey: ['withdrawal-detail', reviewId],
    queryFn: () => withdrawalsApi.get(reviewId ?? ''),
    enabled: !!reviewId,
    refetchInterval: 30_000,
  });

  const bankEvidence = useQuery({ queryKey: ['withdrawal-bank-evidence', reviewId, detail.data?.status],
    queryFn: () => withdrawalsApi.bankEvidence(reviewId ?? ''), enabled: !!reviewId && detail.data?.provider === 'GURUPAY_VIBAN' });

  const close = (): void => {
    setScreeningReady(false);
    setAction(null);
    setSelected(null);
    setReason('');
    setBankReference('');
    setFailure(null);
  };

  const settled = (message: string): void => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['withdrawal-detail'] });
    setOutcome(message);
    void queryClient.invalidateQueries({ queryKey: ['withdrawals'] });
    close();
  };

  const failed = (err: unknown): void => {
    setFailure(err instanceof Error ? err.message : 'That could not be completed.');
  };

  const approve = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      withdrawalsApi.approve(input.id, input.reason),
    onSuccess: (row) =>
      settled(
        `Approved. ${money(row.agreedFee?.totalDebitCents ?? row.amountCents, row.currency)} debited${row.agreedFee ? ' including commission' : ''}. ${whereTheMoneyIs(row.status)}`,
      ),
    onError: failed,
  });

  const markSent = useMutation({
    mutationFn: (input: { id: string; bankReference: string }) =>
      withdrawalsApi.markSent(input.id, input.bankReference),
    onSuccess: () => settled('Recorded as sent.'),
    onError: failed,
  });
  const bankAction = useMutation({
    mutationFn: (input: { id: string; execute: boolean }) => input.execute ? withdrawalsApi.execute(input.id) : withdrawalsApi.refreshBank(input.id),
    onSuccess: (row) => settled(whereTheMoneyIs(row.status)), onError: failed,
  });

  const reject = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      withdrawalsApi.reject(input.id, input.reason),
    onSuccess: () => settled('Refused. Nothing was debited.'),
    onError: failed,
  });

  const markFailed = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      withdrawalsApi.markFailed(input.id, input.reason),
    onSuccess: (row) =>
      settled(
        `Marked failed. ${money(row.amountCents, row.currency)} has been returned to the customer.`,
      ),
    onError: failed,
  });

  const busy = approve.isPending || markSent.isPending || reject.isPending || markFailed.isPending || bankAction.isPending;

  const open = (row: WithdrawalRequest, kind: ActionKind): void => {
    setScreeningReady(false);
    setSelected(row);
    setAction(kind);
    setReason('');
    setBankReference('');
    setFailure(null);
  };

  // "Approve" is the only decision that can stand on its own — the officer is
  // agreeing with what the customer asked for. Every other one is a departure
  // from it, and departures need a reason somebody can read back.
  const reasonRequired = action === 'reject' || action === 'failed';
  const reasonTooShort = reasonRequired && reason.trim().length < 4;
  const referenceMissing = action === 'sent' && bankReference.trim().length < 2;

  const submit = (): void => {
    if (selected === null || action === null) return;
    setFailure(null);
    if (action === 'approve') approve.mutate({ id: selected.id, reason });
    else if (action === 'sent') markSent.mutate({ id: selected.id, bankReference });
    else if (action === 'reject') reject.mutate({ id: selected.id, reason });
    else markFailed.mutate({ id: selected.id, reason });
  };

  const rows = queue.data ?? [];

  return (
    <Box>
      {!reviewId && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5">Withdrawals</Typography>
              <Typography variant="body2" color="text.secondary">
                Review customer transfer requests and track their execution at the assigned bank.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              label="Show"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {FILTERS.map((f) => (
                <MenuItem key={f.value} value={f.value}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {outcome !== null && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOutcome(null)}>
              {outcome}
            </Alert>
          )}
          {queue.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              The queue could not be read. Nothing has been sent or debited.
            </Alert>
          )}
          {!canWork && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can see what is waiting. Reviewing a withdrawal is AML / MLRO work; sending one is the client manager's.
            </Alert>
          )}

          <Paper>
            {queue.isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">Nothing is waiting.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Requested</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Beneficiary</TableCell>
                    <TableCell>Bank reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(row.requestedAt).toLocaleDateString(APP_LOCALE)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(row.requestedAt).toLocaleTimeString(APP_LOCALE)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          component={RouterLink}
                          to={`/customers/${row.customerId}`}
                          variant="body2"
                          sx={{ color: 'primary.main' }}
                        >
                          {row.customerId.slice(0, 8)}…
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.provider}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {money(row.amountCents, row.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.beneficiaryName}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {row.beneficiaryAccount}
                          {row.beneficiaryBic !== null && row.beneficiaryBic !== ''
                            ? ` · ${row.beneficiaryBic}`
                            : ''}
                        </Typography>
                        {row.reference !== null && row.reference !== '' && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Ref: {row.reference}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.bankReference !== null && row.bankReference !== '' ? (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {row.bankReference}
                          </Typography>
                        ) : row.status === 'APPROVED' ? (
                          // Our books say the money is gone and the bank has never
                          // heard of it. That is worth saying out loud.
                          <Chip size="small" color="warning" label="Not sent yet" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={whereTheMoneyIs(row.status)}>
                          <Chip size="small" color={statusColor(row.status)} label={row.status} />
                        </Tooltip>
                        {row.decisionReason !== null && row.decisionReason !== '' && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ maxWidth: 220 }}
                          >
                            {row.decisionReason}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {canReview && row.status === 'REQUESTED' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => open(row, 'reject')} color="error">
                              Refuse
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => open(row, 'approve')}
                            >
                              Approve
                            </Button>
                          </Stack>
                        )}
                        {(row.status === 'APPROVED' ? canSend : canWork) && row.provider === 'GURUPAY_VIBAN' && ['APPROVED','SUBMITTING','SUBMITTED','EXECUTION_UNKNOWN'].includes(row.status) && (
                          <Button disabled={busy} onClick={() => bankAction.mutate({ id: row.id, execute: row.status === 'APPROVED' })}>
                            {row.status === 'APPROVED' ? 'Submit to GuruPay' : 'Check bank status'}
                          </Button>
                        )}
                        {canSend && row.status === 'BANK_REJECTED' && <Button color="error" onClick={() => open(row,'failed')}>Review refund</Button>}
                        {canSend && row.provider !== 'GURUPAY_VIBAN' && row.status === 'APPROVED' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => open(row, 'failed')} color="error">
                              Bank refused it
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => open(row, 'sent')}
                            >
                              Mark sent
                            </Button>
                          </Stack>
                        )}
                        {canSend && row.provider !== 'GURUPAY_VIBAN' && row.status === 'SENT' && (
                          <Button size="small" onClick={() => open(row, 'failed')} color="error">
                            It came back
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
      <Drawer
        anchor="right"
        open={!!reviewId && action === null}
        onClose={onReviewClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, maxWidth: '100vw', p: 3 } }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5">Withdrawal details</Typography>
            <Button onClick={onReviewClose}>Close</Button>
          </Stack>
          {detail.isPending && <CircularProgress aria-label="Loading withdrawal" />}
          {detail.isError && (
            <Alert severity="error">Withdrawal details could not be loaded.</Alert>
          )}
          {outcome && (
            <Alert severity="success" onClose={() => setOutcome(null)}>
              {outcome}
            </Alert>
          )}
          {failure && <Alert severity="error">{failure}</Alert>}
          {detail.data && (
            <>
              <Typography variant="h5">
                {money(detail.data.amountCents, detail.data.currency)}
              </Typography>
              {detail.data.agreedFee && <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 1, m: 0, '& dd': { m: 0, textAlign: 'right', overflowWrap: 'anywhere' } }}>
                <Typography component="dt">Agreed commission</Typography><Typography component="dd">{money(detail.data.agreedFee.feeCents, detail.data.currency)}</Typography>
                <Typography component="dt">Total customer debit</Typography><Typography component="dd" fontWeight={700}>{money(detail.data.agreedFee.totalDebitCents, detail.data.currency)}</Typography>
                <Typography component="dt">Commission account</Typography><Typography component="dd">{detail.data.agreedFee.feeAccountIban || detail.data.agreedFee.feeAccountId}</Typography>
              </Box>}
              <Chip
                sx={{ alignSelf: 'flex-start' }}
                color={statusColor(detail.data.status)}
                label={FILTERS.find(item => item.value === detail.data?.status)?.label ?? detail.data.status}
              />
              <Alert severity="info">{whereTheMoneyIs(detail.data.status)}</Alert>
              <Typography>
                <strong>Beneficiary:</strong> {detail.data.beneficiaryName}
              </Typography>
              <Typography sx={{ overflowWrap: 'anywhere' }}>
                <strong>Account:</strong> {detail.data.beneficiaryAccount}
              </Typography>
              <Typography>
                <strong>Bank:</strong> {detail.data.beneficiaryBank || '—'} ·{' '}
                {detail.data.beneficiaryBic || '—'}
              </Typography>
              <Typography>
                <strong>Reference:</strong> {detail.data.reference || '—'}
              </Typography>
              <Typography>
                <strong>Bank reference:</strong> {detail.data.bankReference || 'Not recorded'}
              </Typography>
              {detail.data.provider === 'GURUPAY_VIBAN' && <Box component="details" sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>Bank instruction and original responses</Box>
                {bankEvidence.isError ? <Alert severity="error">Bank evidence could not be loaded.</Alert>
                  : bankEvidence.isPending ? <Typography>Loading evidence…</Typography>
                  : <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>{JSON.stringify(bankEvidence.data, null, 2)}</Box>}
              </Box>}
              <Typography>
                <strong>Requested:</strong> {new Date(detail.data.requestedAt).toLocaleString(APP_LOCALE)}
              </Typography>
              {/* A decision on the case file is in the comments below, with
                  its author and reason (DEV-95); only an older one shows here. */}
              {detail.data.decidedBy && detail.data.decisionOnFile !== true && (
                <Typography>
                  <strong>Decision:</strong> {detail.data.decidedBy} ·{' '}
                  {detail.data.decisionReason || '—'}
                </Typography>
              )}
              <Button
                component={RouterLink}
                to={`/customers/${detail.data.customerId}`}
                sx={{ alignSelf: 'flex-start' }}
              >
                View customer
              </Button>
              {detail.data.infoRequestMessage && (
                <Alert severity={detail.data.infoAnswer ? 'success' : 'info'}>
                  <strong>Asked:</strong> {detail.data.infoRequestMessage}
                  {detail.data.infoAnswer ? (
                    <>
                      <br />
                      <strong>They answered:</strong> {detail.data.infoAnswer}
                    </>
                  ) : (
                    <>
                      <br />
                      Waiting on the customer.
                    </>
                  )}
                </Alert>
              )}
              {canWork && (
                <CounterpartyHistoryPanel key={`counterparty-${detail.data.id}`} withdrawalId={detail.data.id} />
              )}
              {can('aml.work') && (
                <RaiseSuspicionPanel
                  key={`suspicion-${detail.data.id}`}
                  customerId={detail.data.customerId}
                  withdrawalId={detail.data.id}
                />
              )}
              <ReviewDocumentsPanel key={detail.data.id} kind="withdrawal" id={detail.data.id} canWrite={canReview} />
              <PaymentConfirmationPanel key={`confirmation-${detail.data.id}`} id={detail.data.id} status={detail.data.status} />
              {canWork && <OutgoingCommissionPanel key={`commission-${detail.data.id}`} withdrawalId={detail.data.id} canEditCommission={can('fees.edit')} canEditAmount={canReview} />}
              {canWork && (
                <Stack direction="row" spacing={1}>
                  {canReview && detail.data.status === 'REQUESTED' && (
                    <>
                      <Button color="error" onClick={() => open(detail.data, 'reject')}>
                        Refuse
                      </Button>
                      {/* Refusing a payment to ask for an invoice costs the
                          customer their authentication and their quote. */}
                      <Button onClick={() => setAsking(detail.data.id)}>Ask the customer</Button>
                      <Button variant="contained" onClick={() => open(detail.data, 'approve')}>
                        Review approval
                      </Button>
                    </>
                  )}
                  {(detail.data.status === 'APPROVED' ? canSend : canWork) && detail.data.provider === 'GURUPAY_VIBAN' && ['APPROVED','SUBMITTING','SUBMITTED','EXECUTION_UNKNOWN'].includes(detail.data.status) && (
                    <Button disabled={busy} onClick={() => bankAction.mutate({ id: detail.data.id, execute: detail.data.status === 'APPROVED' })}>
                      {detail.data.status === 'APPROVED' ? 'Submit to GuruPay' : 'Check bank status'}
                    </Button>
                  )}
                  {canSend && detail.data.status === 'BANK_REJECTED' && <Button color="error" onClick={() => open(detail.data,'failed')}>Review refund</Button>}
                  {canSend && detail.data.provider !== 'GURUPAY_VIBAN' && detail.data.status === 'APPROVED' && (
                    <>
                      <Button color="error" onClick={() => open(detail.data, 'failed')}>
                        Bank refused it
                      </Button>
                      <Button variant="contained" onClick={() => open(detail.data, 'sent')}>
                        Record bank transfer
                      </Button>
                    </>
                  )}
                  {canSend && detail.data.provider !== 'GURUPAY_VIBAN' && detail.data.status === 'SENT' && (
                    <Button color="error" onClick={() => open(detail.data, 'failed')}>
                      Review failed transfer
                    </Button>
                  )}
                </Stack>
              )}
              <CommentsPanel
                subjectType="WITHDRAWAL"
                subjectId={detail.data.id}
                customerId={detail.data.customerId}
                canWrite={can('comments.write')}
                dense
              />
            </>
          )}
        </Stack>
      </Drawer>
      <Dialog open={action !== null} onClose={busy ? undefined : close} fullWidth maxWidth="sm">
        <DialogTitle>{action === null ? '' : TITLES[action]}</DialogTitle>
        <DialogContent>
          {selected !== null && action !== null && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {money(selected.amountCents, selected.currency)} to {selected.beneficiaryName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: 'monospace' }}
                >
                  {selected.beneficiaryAccount}
                </Typography>
              </Box>

              {action === 'approve' && (
                <Alert severity="warning">
                  The customer is debited on approval. {selected.provider === 'GURUPAY_VIBAN'
                    ? 'The approved instruction is then submitted to GuruPay. Follow its bank status here; do not send a separate transfer manually.'
                    : 'Send the approved instruction through the assigned bank and record its bank reference here.'}
                </Alert>
              )}
              {action === 'sent' && (
                <Alert severity="info">
                  Nothing about the customer&rsquo;s balance changes here. This records that the
                  transfer was actually made, so it can be traced afterwards.
                </Alert>
              )}
              {action === 'reject' && (
                <Alert severity="info">
                  Nothing has been debited, and nothing will be. The customer sees the reason.
                </Alert>
              )}
              {action === 'failed' && (
                <Alert severity="warning">
                  The full amount goes <strong>back</strong> to the customer&rsquo;s account. Use
                  this only when the bank did not send the money — if it was sent and later
                  returned, that is an incoming payment, not this.
                </Alert>
              )}

              {action === 'approve' && (
                <PaymentScreeningPanel
                  key={`screening-${selected.id}`}
                  path={`/admin/withdrawals/${selected.id}`}
                  onReady={setScreeningReady}
                />
              )}

              <ReviewDocumentsPanel key={`documents-${selected.id}`} kind="withdrawal" id={selected.id} canWrite={canReview} />

              {action === 'sent' && (
                <TextField
                  label="The bank's own reference"
                  value={bankReference}
                  onChange={(e) => setBankReference(e.target.value)}
                  error={bankReference.trim().length > 0 && referenceMissing}
                  helperText="Copy it from the bank's interface. It is how this transfer is traced."
                  autoFocus
                />
              )}

              {action !== 'sent' && (
                <TextField
                  label={reasonRequired ? 'Reason' : 'Note (optional)'}
                  multiline
                  minRows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  error={reason.trim().length > 0 && reasonTooShort}
                  helperText={
                    reasonTooShort
                      ? 'At least 4 characters — the customer will ask why.'
                      : 'Kept against the request, on the client file.'
                  }
                />
              )}

              {failure !== null && <Alert severity="error">{failure}</Alert>}

              {/* No comment box here, deliberately. This dialog already has a
                  Reason, which the decision button saves. A second box beside
                  it that looks the same but is saved by its own button is how
                  an officer types a comment, presses Refuse, and loses it —
                  which is exactly what was reported. The incoming queue keeps
                  its comments in the detail drawer and its reason here; this
                  now does the same, so the two screens behave identically. */}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'reject' || action === 'failed' ? 'error' : 'primary'}
            onClick={submit}
            disabled={
              busy ||
              reasonTooShort ||
              referenceMissing ||
              (action === 'approve' && !screeningReady)
            }
          >
            {action === 'approve'
              ? 'Approve and debit'
              : action === 'sent'
                ? 'Record as sent'
                : action === 'reject'
                  ? 'Refuse'
                  : 'Return the money'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={asking !== null} onClose={() => setAsking(null)} fullWidth maxWidth="sm">
        <DialogTitle>Ask the customer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              The payment stays where it is and keeps its verification. Nothing is decided by
              asking.
            </Typography>
            <TextField
              label="What do you need from them"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              multiline
              minRows={3}
              required
              inputProps={{ maxLength: 2000 }}
              helperText="They see this exactly as written."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsking(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={question.trim().length < 10 || busy}
            onClick={() => {
              if (asking === null) return;
              withdrawalsApi
                .requestInfo(asking, question.trim())
                .then(() => {
                  setAsking(null);
                  setQuestion('');
                  settled('The customer has been asked.');
                })
                .catch(failed);
            }}
          >
            Send it
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
